import React, { useState, useEffect } from "react";
import { useAddNewUserMutation } from "../usersApiSlice";
import { NavLink, useNavigate } from "react-router-dom";
import SignupImg from "../../../assets/bg-ropeswing.png";
import Logo from "../../../shared/design/Logo";

const NAME_REGEX = /^[a-zA-Z-,]+(\s{0,1}[a-zA-Z-, ])*$/;
const EMAIL_REGEX = /^\w+@[a-zA-Z_]+?\.[a-zA-Z]{2,3}$/;
const PWD_REGEX = /^[A-z0-9!@#$%]{8,12}$/;

const STRENGTH_COLORS = ["#C8623A", "#C08A2D", "#1F8A5B"];
const STRENGTH_LABELS = ["Weak", "Good", "Strong"];

const SignUp = () => {
  const [addNewUser, { isLoading, isSuccess, error }] = useAddNewUserMutation();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [validName, setValidName] = useState(false);
  const [email, setEmail] = useState("");
  const [validEmail, setValidEmail] = useState(false);
  const [password, setPassword] = useState("");
  const [validPassword, setValidPassword] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [validPasswordConfirm, setValidPasswordConfirm] = useState(false);
  const [role] = useState("user");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => setValidName(NAME_REGEX.test(name)), [name]);
  useEffect(() => setValidEmail(EMAIL_REGEX.test(email)), [email]);
  useEffect(() => setValidPassword(PWD_REGEX.test(password)), [password]);
  useEffect(
    () => setValidPasswordConfirm(validPassword && passwordConfirm === password),
    [passwordConfirm, password, validPassword]
  );

  useEffect(() => {
    if (isSuccess) {
      setEmail("");
      setPassword("");
      navigate("/");
      navigate(0);
    }
  }, [isSuccess, navigate]);

  const canSave =
    [role, validName, validEmail, validPassword, validPasswordConfirm].every(
      Boolean
    ) && !isLoading;

  const onSaveUserClicked = async (e: React.FormEvent<HTMLFormElement>) => {
    setErrMsg("");
    e.preventDefault();
    if (canSave) {
      await addNewUser({ name, email, password, passwordConfirm, role });
    } else {
      if (!validPasswordConfirm) setErrMsg("- Passwords entered do not match.");
      if (!validPassword) setErrMsg("- Invalid password entered.");
      if (!validEmail) setErrMsg("- Invalid email entered.");
      if (!validName) setErrMsg("- Invalid name entered.");
    }
  };

  // Duplicate-email handling from the server response.
  if (error && errMsg.length <= 1) {
    if ("data" in error) {
      const errData = error.data as { status: string; message: string };
      const message = errData?.message ?? "null";
      if (message.includes("email")) {
        setValidEmail(false);
        setErrMsg(
          "Email address entered is already associated with an account."
        );
      }
    } else {
      console.log("Error:", error);
    }
  }

  const fieldClass = (value: string, valid: boolean) =>
    `wn-field ${value ? (valid ? "ok" : "bad") : ""}`;

  // Password strength: 0–3 bars.
  let strength = 0;
  if (password.length >= 8) strength++;
  if (/[0-9]/.test(password) && /[A-Za-z]/.test(password)) strength++;
  if (/[!@#$%]/.test(password) || password.length >= 11) strength++;

  const emailIsDup = errMsg.includes("associated");

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Image side */}
      <div className="relative hidden flex-col justify-between overflow-hidden p-10 text-white lg:flex">
        <img
          src={SignupImg}
          alt="Wild coastline"
          className="wn-ken absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(19,58,69,0.78)] to-[rgba(19,58,69,0.35)]" />
        <Logo tone="light" className="relative" />
        <div className="relative">
          <h2 className="mb-3 font-serif text-[40px] font-normal leading-[1.08]">
            Book your next
            <br />
            journey today.
          </h2>
          <p className="mb-6 max-w-[380px] text-[16px] text-[#dde7e3]">
            Join thousands of wanderers. Save trips, manage bookings, and be
            first to hear when new expeditions open.
          </p>
          <NavLink
            to="/tours"
            className="inline-block rounded-full border border-white/30 bg-white/15 px-[22px] py-3 text-[14.5px] font-semibold text-white no-underline backdrop-blur-sm"
          >
            Browse tours first →
          </NavLink>
        </div>
        <div className="relative font-mono text-[12px] tracking-[0.04em] text-white/80">
          ⌖ 20.7°N 156.3°W · MAUI, HI
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-8 md:p-10">
        <div className="w-full max-w-[410px]">
          <div className="mb-9 text-right text-[13.5px] text-muted">
            Already have an account?{" "}
            <NavLink
              to="/login"
              className="font-bold text-secondary-400 no-underline"
            >
              Sign in
            </NavLink>
          </div>

          <h1 className="mb-1.5 font-serif text-[38px] font-normal tracking-[-0.01em]">
            Create your account
          </h1>
          <p className="mb-7 text-[15px] text-muted">
            Takes less than a minute.
          </p>

          <form onSubmit={onSaveUserClicked}>
            {/* Name */}
            <label className="mb-1.5 block text-[12px] font-bold tracking-[0.04em] text-muted">
              FULL NAME
            </label>
            <input
              className={fieldClass(name, validName)}
              placeholder="Jane Wanderer"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <div className="mb-2 mt-1.5 min-h-[17px] text-[12px] text-hard">
              {name && !validName ? "Enter your name using letters only." : ""}
            </div>

            {/* Email */}
            <label className="mb-1.5 block text-[12px] font-bold tracking-[0.04em] text-muted">
              EMAIL
            </label>
            <input
              type="email"
              className={fieldClass(email, validEmail)}
              placeholder="jane@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <div className="mb-2 mt-1.5 min-h-[17px] text-[12px] text-hard">
              {emailIsDup
                ? errMsg
                : email && !validEmail
                ? "That doesn't look like a valid email."
                : ""}
            </div>

            {/* Password */}
            <label className="mb-1.5 block text-[12px] font-bold tracking-[0.04em] text-muted">
              PASSWORD
            </label>
            <input
              type="password"
              className={fieldClass(password, validPassword)}
              placeholder="8–12 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div className="mb-1.5 mt-2 flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-1 flex-1 rounded"
                  style={{
                    background:
                      password && i < strength
                        ? STRENGTH_COLORS[strength - 1]
                        : "#e3ddcf",
                  }}
                />
              ))}
            </div>
            <div
              className="mb-2 min-h-[17px] text-[12px]"
              style={{
                color: !password
                  ? "#94a09a"
                  : !validPassword
                  ? "#C8623A"
                  : STRENGTH_COLORS[strength - 1],
              }}
            >
              {!password
                ? "Use 8–12 characters with letters & numbers."
                : !validPassword
                ? "Must be 8–12 characters (letters, numbers, !@#$%)."
                : `${STRENGTH_LABELS[strength - 1]} password.`}
            </div>

            {/* Confirm */}
            <label className="mb-1.5 block text-[12px] font-bold tracking-[0.04em] text-muted">
              CONFIRM PASSWORD
            </label>
            <input
              type="password"
              className={fieldClass(passwordConfirm, validPasswordConfirm)}
              placeholder="Re-enter password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
            />
            <div className="mb-3.5 mt-1.5 min-h-[17px] text-[12px] text-hard">
              {passwordConfirm && !validPasswordConfirm
                ? "Passwords don't match."
                : ""}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-[13px] bg-secondary-400 py-4 text-[16px] font-bold text-white transition-colors hover:bg-ink disabled:opacity-60"
            >
              {isLoading ? "Creating your account…" : "Create account"}
            </button>
          </form>

          <p className="mt-3.5 text-center text-[12px] leading-relaxed text-muted-300">
            By creating an account you agree to our{" "}
            <NavLink to="/signup" className="text-muted">
              Terms
            </NavLink>{" "}
            &amp;{" "}
            <NavLink to="/signup" className="text-muted">
              Privacy Policy
            </NavLink>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
